/**
 * 게시판 관리 (migration 102, 2026-08-18).
 *
 * 관리자는 **전 게시판**(회사·부서·팀·개인)을 여기서 본다. 사용자 화면의 설정 모달과 같은
 * 항목을 다루되, 남이 만든 게시판까지 손댈 수 있다는 점이 다르다.
 *
 * 상단의 두 스위치가 기관 정책이다:
 *   · 기본 전체 전송 — 새로 만드는 게시판·새 글의 알림 기본값을 "전체 전송" 으로 둘지
 *   · 사용자 게시판 생성 허용 — 끄면 일반 사용자는 게시판을 못 만든다(서버가 403)
 *
 * 구조·클래스는 MenuRegistrySection 을 그대로 따른다(섹션 + 하단 모달, ServiceModules.css).
 */
import { useCallback, useEffect, useState } from 'react';
import './console-shared.css';
// Megaphone — 콘솔은 아이콘 레지스트리 렌더러(RenderIcon)로 그렸는데 그 표는 BFF 소유라
//   못 옮긴다. 이름이 'Megaphone' 리터럴 하나뿐이라 lucide 아이콘을 직접 쓴다(결과 동일).
import { Trash2, Megaphone } from '../../icons';
import {
  listAdminBoards, updateAdminBoard, deleteAdminBoard, updateBoardSettings,
  type AdminBoardRow, type AdminBoardPatch, type BoardSettings,
} from '../../api/admin-boards';
// 채택 3단계 — 스위치·칸·고르개·단추·모달을 정본 공통 컴포넌트로(B1·B2·B4·B7·D1·F1).
import { Button, FormField, Input, Modal, ModalActions, Select, Switch } from '../../components/shared';

const SCOPE_LABEL: Record<string, string> = {
  COMPANY: '회사', DIVISION: '부서', TEAM: '팀', PERSONAL: '개인',
};

export function BoardAdminSection() {
  const [list, setList] = useState<AdminBoardRow[]>([]);
  const [settings, setSettings] = useState<BoardSettings>({ defaultNotifyAll: true, allowUserBoardCreate: true, updatedAt: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminBoardRow | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listAdminBoards();
      setList(r.boards);
      setSettings(r.settings);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? '게시판 목록 불러오기 실패');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function patchSettings(patch: Partial<Omit<BoardSettings, 'updatedAt'>>) {
    try { setSettings(await updateBoardSettings(patch)); }
    catch (e: any) { setError(e?.response?.data?.message ?? '정책 변경 실패'); }
  }

  async function toggleNotify(b: AdminBoardRow) {
    setBusyId(b.id);
    try {
      const u = await updateAdminBoard(b.id, { notifyEnabled: !b.notifyEnabled });
      setList((arr) => arr.map((x) => (x.id === b.id ? { ...x, ...u } : x)));
    } catch (e: any) { setError(e?.response?.data?.message ?? '변경 실패'); }
    finally { setBusyId(null); }
  }

  async function remove(b: AdminBoardRow) {
    if (b.systemKey) { setError('기본 회사 게시판은 삭제할 수 없습니다.'); return; }
    if (!window.confirm(`게시판 "${b.name}"을(를) 삭제할까요? 글도 함께 보이지 않게 됩니다.`)) return;
    setBusyId(b.id);
    try { await deleteAdminBoard(b.id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.message ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  return (
    <section className="svc-cat svc-cat--menu">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Megaphone size={17} /></span>
        <span className="svc-cat__title">게시판 관리</span>
        <span className="svc-cat__count">{list.length}</span>
        <span className="svc-cat__desc">회사·부서·팀·개인 게시판 전체와 알림 정책 — 끄면 그 게시판의 글 알림이 나가지 않습니다</span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <div className="svc-card" style={{ marginBottom: 12 }}>
        <div className="svc-card__top">
          <span className="svc-card__name">기본 알림 정책</span>
        </div>
        <div className="svc-card__foot">
          <span className="svc-card__meta">새 게시판·새 글을 기본 전체 전송으로</span>
          <span style={{ marginLeft: 'auto' }}>
            <Switch
              checked={settings.defaultNotifyAll}
              onCheckedChange={(v) => void patchSettings({ defaultNotifyAll: v })}
              aria-label="기본 전체 전송"
            />
          </span>
        </div>
        <div className="svc-card__foot">
          <span className="svc-card__meta">일반 사용자의 게시판 생성 허용</span>
          <span style={{ marginLeft: 'auto' }}>
            <Switch
              checked={settings.allowUserBoardCreate}
              onCheckedChange={(v) => void patchSettings({ allowUserBoardCreate: v })}
              aria-label="사용자 게시판 생성 허용"
            />
          </span>
        </div>
      </div>

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : list.length === 0 ? (
        <p className="svc-empty">등록된 게시판이 없습니다.</p>
      ) : (
        <div className="svc-grid">
          {list.map((b) => {
            const busy = busyId === b.id;
            return (
              <div key={b.id} className={`svc-card${b.notifyEnabled ? '' : ' is-off'}`}>
                <div className="svc-card__top">
                  <span className="svc-card__name" title={b.name}>{b.name}</span>
                  <Switch
                    checked={b.notifyEnabled}
                    disabled={busy}
                    onCheckedChange={() => void toggleNotify(b)}
                    aria-label={b.notifyEnabled ? '알림 끄기' : '알림 켜기'}
                  />
                </div>
                <div className="svc-card__detail">{b.description ?? '설명 없음'}</div>
                <div className="svc-card__chips">
                  <span className="svc-card__chip">{SCOPE_LABEL[b.scopeType] ?? b.scopeType}{b.scopeName ? ` · ${b.scopeName}` : ''}</span>
                  <span className="svc-card__chip">{b.boardType === 'FEED' ? '피드' : '클래식'}</span>
                  <span className="svc-card__chip">{b.isPrivate ? '비공개' : '공개'}</span>
                  {b.systemKey && <span className="svc-card__chip">기본</span>}
                </div>
                <div className="svc-card__foot">
                  <span className="svc-card__meta">
                    글 {b.postCount} · 멤버 {b.memberCount}{b.owner ? ` · ${b.owner.name}` : ''}
                  </span>
                  <Button
                    variant="secondary"
                    size="compact"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => setEditing(b)}
                  >
                    설정
                  </Button>
                  <Button
                    variant="quiet-danger"
                    size="compact"
                    iconOnly
                    disabled={busy || !!b.systemKey}
                    onClick={() => void remove(b)}
                    aria-label={`${b.name} 삭제`}
                    iconLeft={<Trash2 size={13} aria-hidden />}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <BoardModal
          board={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload(); }}
        />
      )}
    </section>
  );
}

function BoardModal({ board, onClose, onSaved }: {
  board: AdminBoardRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description ?? '');
  const [boardType, setBoardType] = useState(board.boardType);
  const [isPrivate, setIsPrivate] = useState(board.isPrivate);
  const [showSecretTitles, setShowSecretTitles] = useState(board.showSecretTitles);
  const [allowAnonymous, setAllowAnonymous] = useState(board.allowAnonymous);
  const [showMailButton, setShowMailButton] = useState(board.showMailButton);
  const [allowComments, setAllowComments] = useState(board.allowComments);
  const [usePostReaders, setUsePostReaders] = useState(board.usePostReaders);
  const [notifyEnabled, setNotifyEnabled] = useState(board.notifyEnabled);
  const [notifyAllByDefault, setNotifyAllByDefault] = useState(board.notifyAllByDefault);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ★ Esc 는 정본 `Modal` 이 듣는다(`useEscapeClose`) — 여기서 또 들으면 두 번 닫는다.
   *   뒤 화면 스크롤 잠금만 남긴다(모달 셸은 제 안쪽만 잠근다). */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  async function submit() {
    setSaving(true); setError(null);
    const patch: AdminBoardPatch = {};
    if (name.trim() !== board.name) patch.name = name.trim();
    if ((description || null) !== board.description) patch.description = description || null;
    if (boardType !== board.boardType) patch.boardType = boardType;
    if (isPrivate !== board.isPrivate) patch.isPrivate = isPrivate;
    if (showSecretTitles !== board.showSecretTitles) patch.showSecretTitles = showSecretTitles;
    if (allowAnonymous !== board.allowAnonymous) patch.allowAnonymous = allowAnonymous;
    if (showMailButton !== board.showMailButton) patch.showMailButton = showMailButton;
    if (allowComments !== board.allowComments) patch.allowComments = allowComments;
    if (usePostReaders !== board.usePostReaders) patch.usePostReaders = usePostReaders;
    if (notifyEnabled !== board.notifyEnabled) patch.notifyEnabled = notifyEnabled;
    if (notifyAllByDefault !== board.notifyAllByDefault) patch.notifyAllByDefault = notifyAllByDefault;
    try {
      if (Object.keys(patch).length > 0) await updateAdminBoard(board.id, patch);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? '저장 실패');
    } finally { setSaving(false); }
  }

  return (
    <Modal
      title="게시판 설정"
      onClose={onClose}
      size="sm"
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={saving}>취소</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={saving} loading={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </ModalActions>
      )}
    >
      <FormField label="제목">
        <Input value={name} onChange={(e) => setName(e.target.value)} fullWidth />
      </FormField>
      <FormField label="설명">
        <Input value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
      </FormField>
      <FormField label="유형">
        <Select
          value={boardType}
          onChange={(v) => setBoardType(v as typeof boardType)}
          options={[{ value: 'CLASSIC', label: '클래식' }, { value: 'FEED', label: '피드' }]}
        />
      </FormField>

      {/* ★ 스위치의 라벨은 스위치 자신이 인다 — <label> 안에 <label> 을 넣지 않는다.
             (지역 스위치는 체크박스라 감싸도 됐지만, 정본 스위치는 제 라벨을 갖는 단추다.) */}
      <Switch checked={isPrivate} onCheckedChange={setIsPrivate} label="비공개" />
      <Switch checked={showSecretTitles} onCheckedChange={setShowSecretTitles} label="비공개 문서 제목 표시" />
      <Switch checked={allowAnonymous} onCheckedChange={setAllowAnonymous} label="익명 글 허용" />
      <Switch checked={showMailButton} onCheckedChange={setShowMailButton} label="메일발송 버튼 표시" />
      <Switch checked={allowComments} onCheckedChange={setAllowComments} label="댓글 작성 허용" />
      <Switch checked={usePostReaders} onCheckedChange={setUsePostReaders} label="게시글 별 열람자 설정" />
      <Switch checked={notifyEnabled} onCheckedChange={setNotifyEnabled} label="알림 사용" />
      <Switch checked={notifyAllByDefault} onCheckedChange={setNotifyAllByDefault} label="기본 전체 전송" />

      {error && <div className="svc-test svc-test--bad" role="alert"><span className="svc-dot svc-dot--bad" />{error}</div>}
    </Modal>
  );
}
